'use client';

import * as React from 'react';
import { Check, ChevronsUpDown, User, Loader2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { api } from '@/trpc/react';
import { MODEL_VERSIONS } from '@/_data/constants';
import useInfiniteScroll from 'react-infinite-scroll-hook';

export type ModelItem = {
  id: string;
  name: string;
  avatarUrl?: string | null;
  version: string;
  teams?: { id: string; name: string }[];
  modelRepositoryId?: string;
  modelRepositoryName?: string;
  status?: string;
  teamId?: string;
  // Add other relevant fields as needed
};

type MakeOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export interface ModelSelectorProps {
  selectedModel?: MakeOptional<ModelItem, 'id'>;
  selectedModelId?: string; // Fallback ID for lookup if selectedModel not provided
  onModelChange: (model: ModelItem) => void;
  className?: string;
  isLocked?: boolean;
  isProcessing?: boolean; // Abstracted from trainingStatus
  showConfirmation?: boolean;
  teamId?: string;
  fetchParams?: Record<string, unknown>; // For custom API params like includeTrainingMeta
  placeholder?: string;
  noResultsText?: string;
  errorText?: string;
  loadingText?: string;
}

export function ModelSelector({
  selectedModel: propSelectedModel,
  selectedModelId,
  onModelChange,
  className,
  isLocked = false,
  isProcessing = false,
  showConfirmation = true,
  teamId,
  fetchParams = {},
  placeholder = 'Select model...',
  noResultsText = 'No models found.',
  errorText = 'Failed to load models',
  loadingText = 'Loading models...'
}: ModelSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [pendingModel, setPendingModel] = React.useState<ModelItem | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, refetch } =
    api.model.getUserModels.useInfiniteQuery(
      { teamId, ...fetchParams },
      {
        getNextPageParam: lastPage => lastPage.nextCursor,
        enabled: !isLocked
      }
    );

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: isLocked || isError,
    rootMargin: '0px 0px 400px 0px'
  });

  const models = React.useMemo<ModelItem[]>(() => {
    return (
      data?.pages.flatMap(page =>
        page.models.map(m => ({
          id: m.id,
          name: m.name || m.modelRepositoryName || 'Unnamed',
          avatarUrl: m.avatarUrl || m.selfieUrl || null,
          version: m.version,
          teams: m.teams,
          modelRepositoryId: m.modelRepositoryId,
          modelRepositoryName: m.modelRepositoryName,
          status: m.status,
          teamId: m.teamId!
        }))
      ) ?? []
    );
  }, [data]);

  const effectiveSelectedModel = React.useMemo<MakeOptional<ModelItem, 'id'> | undefined>(() => {
    if (propSelectedModel) {
      return {
        id: propSelectedModel.id || undefined,
        name: propSelectedModel.name,
        avatarUrl: propSelectedModel.avatarUrl,
        version: propSelectedModel.version,
        teams: propSelectedModel.teams,
        modelRepositoryId: propSelectedModel.modelRepositoryId,
        modelRepositoryName: propSelectedModel.modelRepositoryName,
        status: propSelectedModel.status,
        teamId: propSelectedModel.teamId
      };
    }
    return models.find(m => m.id === selectedModelId);
  }, [propSelectedModel, models, selectedModelId]);

  const hasMultipleModels = models.length > 1;

  const handleSelect = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    if (!model || isLocked) return;

    if (showConfirmation && hasMultipleModels && effectiveSelectedModel?.id) {
      setPendingModel(model);
      setShowConfirmDialog(true);
    } else {
      onModelChange(model);
      setOpen(false);
    }
  };

  const confirmChange = () => {
    if (pendingModel) {
      onModelChange(pendingModel);
      setPendingModel(null);
      setShowConfirmDialog(false);
      setOpen(false);
    }
  };

  React.useEffect(() => {
    if (isError) {
      setError(errorText);
    }
  }, [isError, errorText]);

  if (isLocked && effectiveSelectedModel) {
    return (
      <div
        className={cn(
          'flex h-16 items-center gap-2 px-3 rounded-md bg-muted border border-foreground/20 w-full min-w-64 justify-between',
          isProcessing && 'border-amber-500/40',
          className
        )}
      >
        <div className="flex items-center gap-2 justify-start">
          <Avatar className="h-8 w-8 shrink-0 relative">
            <AvatarImage src={effectiveSelectedModel.avatarUrl || ''} alt={effectiveSelectedModel.name} />
            <AvatarFallback>{effectiveSelectedModel.name?.[0] ?? '?'}</AvatarFallback>
            {isProcessing && (
              <div className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-amber-500 animate-pulse" />
            )}
          </Avatar>
          <div className="flex flex-col gap-0 items-start">
            <span className="text-xs text-muted-foreground">Model selected</span>
            <span className="text-base">
              {effectiveSelectedModel.modelRepositoryName ?? effectiveSelectedModel.name ?? 'Unnamed Model'}
            </span>
          </div>
        </div>
        <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={isLocked || isPending}>
          <Button
            ref={triggerRef}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn('w-full justify-between h-auto p-3 bg-transparent', className)}
          >
            {isPending ? (
              <div className="flex items-center gap-3">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-muted-foreground">{loadingText}</span>
              </div>
            ) : error ? (
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-destructive/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-destructive" />
                </div>
                <span className="text-destructive">{error}</span>
              </div>
            ) : effectiveSelectedModel ? (
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={effectiveSelectedModel.avatarUrl || '/placeholder.svg'}
                    alt={effectiveSelectedModel.name}
                  />
                  <AvatarFallback>{effectiveSelectedModel.name?.[0] ?? '?'}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start">
                  <span className="font-medium">
                    {effectiveSelectedModel.modelRepositoryName ?? effectiveSelectedModel.name}
                  </span>
                  {teamId && effectiveSelectedModel.teams && (
                    <span className="text-sm text-muted-foreground">
                      {effectiveSelectedModel.teams.find(t => t.id === teamId)?.name}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <span className="text-muted-foreground">{placeholder}</span>
              </div>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" align="start" style={{ width: triggerRef.current?.offsetWidth || 'auto' }}>
          <Command>
            <CommandInput placeholder="Search models..." />
            <CommandList>
              {isPending ? (
                <div className="flex items-center justify-center p-6">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">{loadingText}</span>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center p-6">
                  <span className="text-sm text-destructive">{error}</span>
                </div>
              ) : models.length === 0 ? (
                <CommandEmpty>{noResultsText}</CommandEmpty>
              ) : (
                <CommandGroup>
                  {models.map(model => (
                    <CommandItem
                      key={model.id}
                      value={`${model.modelRepositoryName ?? model.name} ${
                        model.teams?.map(t => t.name).join(' ') ?? ''
                      } ${model.id}`}
                      onSelect={() => handleSelect(model.id)}
                      disabled={model.version !== MODEL_VERSIONS['flux.1-dev']}
                      className="flex items-center gap-3 p-3"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={model.avatarUrl || '/placeholder.svg'} alt={model.name} />
                        <AvatarFallback>{model.name?.[0] ?? '?'}</AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="font-medium">{model.modelRepositoryName ?? model.name}</span>
                        {teamId && model.teams && (
                          <span className="text-sm text-muted-foreground">
                            {model.teams.find(t => t.id === teamId)?.name || model.teams.map(t => t.name).join(' • ')}
                          </span>
                        )}
                        {model.version !== MODEL_VERSIONS['flux.1-dev'] && (
                          <span className="text-xs text-muted-foreground">(Outdated)</span>
                        )}
                      </div>
                      <Check
                        className={cn(
                          'ml-auto h-4 w-4',
                          effectiveSelectedModel?.id === model.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                  {hasNextPage && (
                    <div ref={sentryRef} className="flex justify-center py-2">
                      {isFetchingNextPage && <Loader2 className="h-4 w-4 animate-spin" />}
                    </div>
                  )}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>Changing the model will affect your current selection. Continue?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-between gap-2">
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              No
            </Button>
            <Button onClick={confirmChange}>Yes, continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {error && (
        <Button
          variant="outline"
          onClick={() => {
            setError(null);
            refetch();
          }}
          className="w-full mt-2"
        >
          Retry Loading Models
        </Button>
      )}
    </div>
  );
}
